import "./IDNowStatus.scss";

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Link,
  Typography,
} from "@material-ui/core";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import InfoIcon from "@material-ui/icons/Info";
import RefreshIcon from "@material-ui/icons/Refresh";
import WatchLaterIcon from "@material-ui/icons/WatchLater";
import { IdNowStatus } from "common/backend/types/utils";
import { GetCurrentUserLazy } from "common/graphql/queryDescriptors";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { StoreState } from "common/store/types";
import { UserManageable } from "common/store/user/types";
import { ContentWrapper } from "components/ContentWrapper";
import Navbar from "components/Navbar";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";

const IDVisual = () =>
  <Box className="id-visual">
    <CheckCircleIcon className="icon" />

    <Grid container>
      <Grid item xs={6} className="account-container">
        <AccountCircleIcon className="account" />
      </Grid>

      <Grid
        item
        xs={6}
        container
        className="lines-container"
        direction="column"
        spacing={1}
      >
        <Grid item className="line-wrapper">
          <div className="line--short" />
        </Grid>

        <Grid item className="line-wrapper">
          <div className="line" />
        </Grid>

        <Grid item className="line-wrapper">
          <div className="line" />
        </Grid>
      </Grid>
    </Grid>
  </Box>
;
const IDNowStatusContent = ({ status }: any) => {
  // State
  const [buttonPressed, setButtonPressed] = useState(false);

  // Hooks
  const { t } = useTranslation();
  const history = useHistory();
  const storageUserDetails = useSelector<StoreState, UserManageable>((state) => state.currentUser);

  const {
    execute: executeGetCurrentUser,
    loading: getCurrentUserLoading,
  } = useCustomQuery(GetCurrentUserLazy({}));

  // Helpers
  const openInNewTab = (url: string) => {
    const newWindow = window.open(
      url,
      "_blank",
      "noopener,noreferrer"
    );
    if (newWindow) {
      newWindow.opener = null;
    }
  };

  // Options
  let title = null;
  let subtitle = null;
  let buttonText = null;
  let icon = null;
  let onClick = undefined;

  switch (status) {
    case IdNowStatus.NEEDS_APPROVAL:
      icon = <WatchLaterIcon className="icon--big" />;
      title = t(`Information Needs Approval`);
      subtitle = t(`Please wait for your information to be verified.`);
      break;
    case IdNowStatus.CREATED:
      title = t(`Verify with IDNow`);
      subtitle =
        t(`Your identification code is`) + ": " + storageUserDetails.idNowId;
      icon = <IDVisual />;
      buttonText = buttonPressed ? <RefreshIcon /> : t("Start");
      onClick = buttonPressed
        ? () => executeGetCurrentUser()
        : () => {
          setButtonPressed(true);
          openInNewTab(`https://go.test.idnow.de/finpro/identifications/${storageUserDetails.idNowTransactionId}`);
        };
      break;
    case IdNowStatus.SUCCESS:
      icon = <CheckCircleIcon className="icon--big" />;
      title = t(`Verification Completed`);
      subtitle = t(`Thanks for completing verification. You’ll receive an email soon with your results and next steps.`);
      buttonText = <ArrowForwardIcon />;
      onClick = () => history.goBack();
      break;
    case IdNowStatus.FRAUD_SUSPICION_CONFIRMED:
      icon = <InfoIcon className="icon--big" />;
      title = t(`More Information Needed`);
      subtitle = t(`Please contact FinPro support to be verified.`);
      break;
    default:
      return <></>;
  }

  return (
    <Box className={status !== IdNowStatus.CREATED ? "grid-container" : ""}>
      {icon}

      <Typography className="title--centered--isolated">{title}</Typography>

      <Typography className="subtitle--centered">{subtitle}</Typography>

      {buttonText &&
        <>
          {getCurrentUserLoading ?
            <CircularProgress />
            :
            <Button className="primary" onClick={onClick} variant="contained">
              {buttonText}
            </Button>
          }
        </>
      }
    </Box>
  );
};

export const IDNowStatus = () => {
  // Hooks
  const storageUserDetails = useSelector<StoreState, UserManageable>((state) => state.currentUser);

  return (
    <Box>
      <Navbar />

      <ContentWrapper>
        <Box className="idnow-status">
          <Card className={"card"}>
            <IDNowStatusContent status={storageUserDetails.idNowStatus} />
          </Card>
        </Box>
      </ContentWrapper>
    </Box>
  );
};

export const ReisterAdviceBanner = ({ status } : any) => {
  // State
  const [visible, setVisible] = React.useState(false);

  // Hooks
  const history = useHistory();
  const { t } = useTranslation();

  useEffect(
    () => {
      const timer = setTimeout(
        () => {
          setVisible(true);
        },
        1000
      );
      return () => clearTimeout(timer);
    },
    []
  );

  if (visible) {
    let banner = null;

    switch (status) {
      case IdNowStatus.NEEDS_APPROVAL:
        break;
      case IdNowStatus.CREATED:
        banner =
          <Box className="register-advice-banner">
            <Typography variant="subtitle2" color="textSecondary">
              <Link className="link" onClick={() => history.push("/idnowstatus")}>{t("Verify your identity")}</Link>
              {t(" to save time and be able to invest immediately or ")}
              <Link className="link" onClick={() => setVisible(false)}>{t("suppress this message")}</Link>
            </Typography>
          </Box>
        ;
        break;
      case IdNowStatus.SUCCESS:
        break;
      case IdNowStatus.FRAUD_SUSPICION_CONFIRMED:
        break;
      default:
        banner =
          <Box className="register-advice-banner">
            <Typography variant="subtitle2" color="textSecondary">
              <Link className="link" onClick={() => history.push("/kyc")}>{t("Verify your identity")}</Link>
              {t(" to save time and be able to invest immediately or ")}
              <Link className="link" onClick={() => setVisible(false)}>{t("suppress this message")}</Link>
            </Typography>
          </Box>
        ;
        break;
    }

    return (
      <Box
        bgcolor="transparent"
        marginY="100px"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Card className={"card"}>
          {banner}
        </Card>
      </Box>
    );
  }

  return null;
}
